import type { ControllerChannelConfig, ChannelInstance, HandshakeEventData } from './types';
import { HandshakeMessage, ROUTE_CHANGE } from './types';
import { ChannelMessaging } from './ChannelMessaging';

const DEFAULT_TIMEOUT = 10000;

export function createControllerChannel(config: ControllerChannelConfig): ChannelInstance {
	const {
		iframe,
		targetOrigin,
		onReady,
		onMessage,
		onError,
		onDisconnect,
		onRouteChange,
		timeout = DEFAULT_TIMEOUT,
	} = config;

	let messaging: ChannelMessaging | null = null;
	let connected = false;
	let timeoutTimer: ReturnType<typeof setTimeout> | null = null;

	const cleanup = () => {
		if (timeoutTimer) {
			clearTimeout(timeoutTimer);
			timeoutTimer = null;
		}
		window.removeEventListener('message', handleMessage);
	};

	const handleMessage = (event: MessageEvent<HandshakeEventData>) => {
		if (event.origin !== targetOrigin) return;

		const message = event.data?.message;

		// Step 1: Client initiates handshake
		if (message === HandshakeMessage.INIT) {
			(event.source as WindowProxy)?.postMessage(
				{ message: HandshakeMessage.RESPONSE },
				{ targetOrigin: event.origin },
			);
			return;
		}

		// Step 2: Client sends port
		if (message === HandshakeMessage.PORT) {
			// Validate source is the expected iframe
			if (event.source !== iframe.contentWindow) {
				onError?.(new Error('Port received from unexpected source'));
				return;
			}

			const [port] = event.ports;
			if (!port) {
				onError?.(new Error('No port received from client'));
				return;
			}

			// Clear handshake timeout
			if (timeoutTimer) {
				clearTimeout(timeoutTimer);
				timeoutTimer = null;
			}

			port.start();
			messaging = new ChannelMessaging(port);
			connected = true;

			if (onMessage) {
				messaging.onAny(onMessage);
			}

			onReady?.();

			if (onRouteChange) {
				messaging.on(ROUTE_CHANGE, (payload) => {
					const path = (payload as { path?: unknown } | null)?.path;
					if (typeof path === 'string') onRouteChange(path);
					else onError?.(new Error('Invalid ROUTE_CHANGE payload'));
				});
			}
		}
	};

	const connect = () => {
		window.addEventListener('message', handleMessage);

		// Set handshake timeout
		timeoutTimer = setTimeout(() => {
			if (!connected) {
				cleanup();
				onError?.(new Error('Handshake timeout'));
			}
		}, timeout);
	};

	// Initial connection
	connect();

	return {
		send: (type: string, payload: unknown, version: number = 1) => {
			if (!messaging) {
				onError?.(new Error('Channel not connected'));
				return;
			}
			messaging.send(type, payload, version);
		},
		on: (type: string, callback: (payload: unknown, version: number) => void) => {
			if (!messaging) {
				onError?.(new Error('Channel not connected'));
				return () => {};
			}
			return messaging.on(type, callback);
		},
		once: (type: string, callback: (payload: unknown, version: number) => void) => {
			if (!messaging) {
				onError?.(new Error('Channel not connected'));
				return () => {};
			}
			return messaging.once(type, callback);
		},
		request: <T = unknown>(type: string, payload: unknown, timeoutMs?: number) => {
			if (!messaging) {
				return Promise.reject(new Error('Channel not connected'));
			}
			return messaging.request<T>(type, payload, timeoutMs);
		},
		reply: (requestId: string, data: unknown) => {
			if (!messaging) {
				onError?.(new Error('Channel not connected'));
				return;
			}
			messaging.reply(requestId, data);
		},
		disconnect: () => {
			cleanup();
			messaging?.disconnect();
			messaging = null;
			connected = false;
			onDisconnect?.();
		},
		reconnect: () => {
			// Clean up existing connection
			cleanup();
			messaging?.disconnect();
			messaging = null;
			connected = false;

			// Re-establish connection
			connect();
		},
		isConnected: () => connected,
	};
}
